/* =========================================================================
   Qikify Sticky Tab section
   Scroll-driven sticky tabs with a sliding "pill" (framer layoutId style).
   Multi-instance safe: every [data-qk-optimize] on the page is initialised.
   Reads its motion config from CSS vars (--qk-header-scroll, --qk-scroll-per-tab)
   and the data-sticky-motion attribute, so no Liquid is needed in here.
   ========================================================================= */
(function () {
  'use strict';

  function initSection(root) {
    if (!root || root.dataset.qkInit === '1') return;
    root.dataset.qkInit = '1';

    var header = root.querySelector('[data-qk-header]');
    var tabs   = Array.prototype.slice.call(root.querySelectorAll('[data-qk-tab]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-qk-panel]'));
    var pill   = root.querySelector('[data-qk-pill]');
    var bar    = root.querySelector('[data-qk-bar]');
    if (!tabs.length) return;

    var COUNT  = tabs.length;
    var motion = root.getAttribute('data-sticky-motion') === 'true';
    var ticking = false;
    /* while a programmatic scroll is in flight, hold the active tab on the
       target so scroll-derived switching can't snap it back mid-scroll */
    var scrollLock = false;
    var lockTarget = -1;
    var lockFrames = 0;

    /* resolve a CSS length token like "90vh" / "120px" to pixels */
    function px(name, fallback) {
      var v = getComputedStyle(root).getPropertyValue(name).trim();
      if (v.indexOf('vh') > -1) return parseFloat(v) / 100 * window.innerHeight;
      if (v.indexOf('px') > -1) return parseFloat(v);
      var n = parseFloat(v);
      return isNaN(n) ? fallback : n;
    }

    /* slide the shared pill onto tab[i]'s box */
    function movePill(i, animate) {
      var t = tabs[i];
      if (!t || !pill) return;
      if (animate === false) pill.style.transition = 'none';
      pill.style.width  = t.offsetWidth + 'px';
      pill.style.height = t.offsetHeight + 'px';
      pill.style.transform = 'translate(' + t.offsetLeft + 'px,' + t.offsetTop + 'px)';
      pill.style.opacity = '1';
      if (animate === false) { void pill.offsetWidth; pill.style.transition = ''; }
    }

    function current() {
      for (var i = 0; i < tabs.length; i++) if (tabs[i].classList.contains('is-active')) return i;
      return 0;
    }

    function setActive(i) {
      tabs.forEach(function (t, x) {
        t.classList.toggle('is-active', x === i);
        t.setAttribute('aria-selected', x === i ? 'true' : 'false');
      });
      panels.forEach(function (p, x) { p.classList.toggle('is-active', x === i); });
      movePill(i, true);
    }

    function metrics() {
      return {
        headerRun: px('--qk-header-scroll', window.innerHeight * 0.55),
        perTab:    px('--qk-scroll-per-tab', window.innerHeight * 0.9),
        start:     root.getBoundingClientRect().top + window.scrollY
      };
    }

    function onScroll() {
      ticking = false;
      if (!motion) return;

      var m = metrics();
      var y = window.scrollY - m.start;

      /* header fade + collapse */
      if (header) {
        var hp = Math.min(Math.max(y / m.headerRun, 0), 1);
        header.style.opacity = String(1 - hp);
        header.style.transform = 'translateY(' + (-30 * hp) + 'px) scale(' + (1 - 0.06 * hp) + ')';
        header.style.maxHeight = (1 - hp) * 500 + 'px';
        header.style.pointerEvents = hp > 0.9 ? 'none' : 'auto';
      }

      /* active tab from scroll progress */
      var tabScroll = y - m.headerRun;
      var total = m.perTab * COUNT;
      var tp = Math.min(Math.max(tabScroll / total, 0), 0.9999);
      var idx = Math.floor(tp * COUNT);
      if (idx < 0) idx = 0;
      if (idx > COUNT - 1) idx = COUNT - 1;

      if (bar) {
        var overall = Math.min(Math.max(y / (m.headerRun + total), 0), 1);
        bar.style.width = (overall * 100) + '%';
      }

      /* stay locked on the clicked tab until the scroll reaches it */
      if (scrollLock) {
        lockFrames++;
        if (idx === lockTarget || lockFrames > 150) { scrollLock = false; lockFrames = 0; }
        else return;
      }
      if (tabScroll >= 0 && idx !== current()) setActive(idx);
    }

    /* jump to a tab's scroll segment and lock until it arrives */
    function goTo(i) {
      var m = metrics();
      scrollLock = true; lockTarget = i; lockFrames = 0;
      setActive(i);
      window.scrollTo({ top: m.start + m.headerRun + m.perTab * (i + 0.5), behavior: 'smooth' });
    }

    function requestScroll() {
      if (!ticking) { window.requestAnimationFrame(onScroll); ticking = true; }
    }

    window.addEventListener('scroll', requestScroll, { passive: true });
    window.addEventListener('resize', function () { movePill(current(), false); onScroll(); });

    /* click a tab: scroll to its segment (motion) or just switch (static) */
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var i = parseInt(tab.getAttribute('data-qk-tab'), 10);
        if (motion) { goTo(i); } else { setActive(i); }
      });
    });

    /* accordion inside each panel (single-open); opening swaps the frame image */
    root.querySelectorAll('[data-qk-acc]').forEach(function (acc) {
      var panel = acc.closest('[data-qk-panel]');
      function showFrameImage(idx) {
        if (!panel || idx == null) return;
        panel.querySelectorAll('[data-qk-frame-img]').forEach(function (img) {
          img.classList.toggle('is-shown', img.getAttribute('data-qk-frame-img') === String(idx));
        });
      }
      acc.querySelectorAll('[data-qk-acc-head]').forEach(function (head) {
        head.addEventListener('click', function () {
          var item = head.closest('[data-qk-acc-item]');
          var open = item.classList.contains('is-open');
          acc.querySelectorAll('[data-qk-acc-item]').forEach(function (it) {
            it.classList.remove('is-open');
            it.querySelector('[data-qk-acc-head]').setAttribute('aria-expanded', 'false');
          });
          if (!open) {
            item.classList.add('is-open');
            head.setAttribute('aria-expanded', 'true');
            showFrameImage(item.getAttribute('data-qk-feature'));
          }
        });
      });
    });

    /* Theme editor: activate the tab whose block (Tab or Feature) is being edited */
    document.addEventListener('shopify:block:select', function (e) {
      if (!root.contains(e.target)) return;
      var btn = e.target.closest('[data-qk-tab]');
      if (btn) {
        var i = parseInt(btn.getAttribute('data-qk-tab'), 10);
        if (motion) { goTo(i); } else { setActive(i); }
        return;
      }
      /* a Feature block: activate its panel's tab, then open that feature */
      var panel = e.target.closest('[data-qk-panel]');
      if (!panel) return;
      var pi = panels.indexOf(panel);
      if (pi > -1) { if (motion) { goTo(pi); } else { setActive(pi); } }
      var item = e.target.closest('[data-qk-acc-item]');
      if (item && !item.classList.contains('is-open')) {
        var head = item.querySelector('[data-qk-acc-head]');
        if (head) head.click();
      }
    });

    movePill(current(), false);
    onScroll();
  }

  function initAll() {
    document.querySelectorAll('[data-qk-optimize]').forEach(initSection);
  }

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', initAll);

  /* re-init when the section is re-rendered in the theme editor */
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-qk-optimize]');
    if (root) { root.dataset.qkInit = ''; initSection(root); }
  });
})();
